import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens, Button, Input } from '@fluentui/react-components';
import { iconMap } from '../theme/icons';
import { stonePalette } from '../theme/brand';

const SearchIcon = iconMap.search;
const ArrowForwardIcon = iconMap.arrow_forward;
const ArrowBackIcon = iconMap.arrow_back;
const HomeIcon = iconMap.home;

const CATEGORIES = [
  { to: '/baldai/spintos-varstomomis-durimis', labelKey: 'notfound.cat_hinged', icon: 'door_front' },
  { to: '/baldai/spintos-stumdomomis-durimis', labelKey: 'notfound.cat_sliding', icon: 'door_sliding' },
  { to: '/baldai/lentynos', labelKey: 'notfound.cat_shelves', icon: 'shelves' },
  { to: '/baldai/spinteles', labelKey: 'notfound.cat_cabinets', icon: 'dresser' },
  { to: '/baldai/komodos', labelKey: 'notfound.cat_dressers', icon: 'night_shelter' },
  { to: '/baldai/tv-spinteles', labelKey: 'notfound.cat_tv', icon: 'tv' },
];

const useStyles = makeStyles({
  page: {
    minHeight: '80vh',
    backgroundColor: stonePalette[50],
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    paddingTop: tokens.spacingVerticalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
  },
  inner: { maxWidth: '42rem', marginLeft: 'auto', marginRight: 'auto', textAlign: 'center' },
  // Gradiento tekstas dekoratyvus (background-clip triukas neturi Fluent atitikmens),
  // bet spalvų stotelės imamos IŠ token'ų, ne raw hex.
  code: {
    fontSize: '120px',
    lineHeight: '1',
    fontWeight: '900',
    userSelect: 'none',
    marginBottom: tokens.spacingVerticalM,
    background: `linear-gradient(135deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorNeutralForeground1} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    '@media (min-width: 640px)': { fontSize: '160px' },
  },
  title: { marginBottom: tokens.spacingVerticalS, fontSize: '1.625rem', lineHeight: '1.25', fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1 },
  subtitle: {
    fontSize: tokens.fontSizeBase400, lineHeight: '1.6', color: tokens.colorNeutralForeground2,
    maxWidth: '20rem', marginLeft: 'auto', marginRight: 'auto', marginBottom: tokens.spacingVerticalXXL,
  },
  searchForm: { display: 'flex', gap: tokens.spacingHorizontalS, maxWidth: '24rem', marginLeft: 'auto', marginRight: 'auto', marginBottom: tokens.spacingVerticalXXL },
  searchInput: { flexGrow: 1 },
  eyebrow: {
    display: 'block', fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: tokens.spacingVerticalM,
  },
  categoryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: tokens.spacingHorizontalS, marginBottom: tokens.spacingVerticalXXL,
    '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
  },
  categoryCard: {
    display: 'block', textAlign: 'left', padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusLarge,
    textDecorationLine: 'none', color: tokens.colorNeutralForeground1,
    transitionProperty: 'box-shadow, color', transitionDuration: tokens.durationNormal,
    ':hover': { boxShadow: tokens.shadow8, color: tokens.colorBrandForeground2 },
  },
  categoryIcon: { display: 'block', fontSize: '22px', color: tokens.colorBrandForeground2, marginBottom: tokens.spacingVerticalXS },
  categoryLabel: { display: 'block', fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightMedium, lineHeight: '1.3', color: 'inherit' },
  navRow: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingHorizontalS, justifyContent: 'center',
    '@media (min-width: 640px)': { flexDirection: 'row' },
  },
});

function NotFound() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const s = useStyles();

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(search.trim() ? `/baldai?search=${encodeURIComponent(search.trim())}` : '/baldai');
  };

  return (
    <div className={s.page}>
      <div className={s.inner}>

        {/* 404 */}
        <div className={s.code}>404</div>
        <h1 className={s.title}>{t('notfound.title')}</h1>
        <p className={s.subtitle}>
          {t('notfound.subtitle')}
        </p>

        {/* Search */}
        <form onSubmit={handleSearch} className={s.searchForm}>
          <Input
            className={s.searchInput}
            contentBefore={<SearchIcon fontSize={18} />}
            placeholder={t('notfound.search_ph')}
            value={search}
            onChange={(_e, data) => setSearch(data.value)}
          />
          <Button type="submit" appearance="primary" icon={<ArrowForwardIcon fontSize={18} />} aria-label={t('notfound.search_aria')} />
        </form>

        {/* Categories */}
        <span className={s.eyebrow}>{t('notfound.categories')}</span>
        <div className={s.categoryGrid}>
          {CATEGORIES.map(({ to, labelKey, icon }) => {
            const Icon = iconMap[icon];
            return (
              <Link key={to} to={to} className={s.categoryCard}>
                {Icon && <Icon className={s.categoryIcon} />}
                <span className={s.categoryLabel}>{t(labelKey)}</span>
              </Link>
            );
          })}
        </div>

        {/* Nav buttons */}
        <div className={s.navRow}>
          <Button appearance="primary" size="large" as={Link} to="/" icon={<HomeIcon fontSize={18} />}>
            {t('notfound.home_btn')}
          </Button>
          <Button appearance="outline" size="large" icon={<ArrowBackIcon fontSize={18} />} onClick={() => navigate(-1)}>
            {t('notfound.back_btn')}
          </Button>
        </div>

      </div>
    </div>
  );
}

export default NotFound;
